import React, { useCallback, useLayoutEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ReactComponent as CloseSvg } from '@assets/icons/cross-xs.svg';
import { apiClient } from '@api/client';
import { useOrganization } from '@components/OrganizationBoundary';
import useOnboardingProcess from '@hooks/useOnboardingProcess';
import { useAuth } from '@hooks/useAuth';
import { useIsDemoOrganization } from '@hooks/useIsDemoOrganization';
import { DEMO_MODE_PANEL_HEIGHT } from '@components/DemoModePanel/DemoModePanel';
import { ANNOUNCEMENT_MEDIA_VIEWER_TOUR_METADATA_KEY } from '@components/Modals/AnnouncementModal/AnnouncementModalContainer';

import './MediaViewerTourBanner.less';

export default function MediaViewerTourBanner() {
  const { user, updateUserProfile } = useAuth(false);
  const isDemoOrganization = useIsDemoOrganization();
  const { currentOrganization } = useOrganization(false);
  const { tourData } = useOnboardingProcess();
  const [_, setSearchParams] = useSearchParams();
  const [isDismissed, setIsDismissed] = useState(() => {
    if (!user || user.type !== 'internal') {
      return (
        localStorage.getItem(ANNOUNCEMENT_MEDIA_VIEWER_TOUR_METADATA_KEY) ===
        'true'
      );
    }
    return !!user.metadata[ANNOUNCEMENT_MEDIA_VIEWER_TOUR_METADATA_KEY];
  });

  const hasActiveTour = !!tourData?.type;

  const isVisible =
    !isDemoOrganization &&
    !isDismissed &&
    !hasActiveTour &&
    !(
      user &&
      user.type === 'internal' &&
      currentOrganization?.entity?.features.disableAnnouncements
    );

  useLayoutEffect(() => {
    if (!isVisible) return undefined;
    document.documentElement.style.setProperty(
      '--demo-mode-panel-width',
      DEMO_MODE_PANEL_HEIGHT
    );
    return () => {
      document.documentElement.style.removeProperty('--demo-mode-panel-width');
    };
  }, [isVisible]);

  const onDismiss = useCallback(async () => {
    setIsDismissed(true);
    if (!user || user.type !== 'internal') {
      localStorage.setItem(ANNOUNCEMENT_MEDIA_VIEWER_TOUR_METADATA_KEY, 'true');
      return;
    }
    updateUserProfile({
      metadata: { [ANNOUNCEMENT_MEDIA_VIEWER_TOUR_METADATA_KEY]: true }
    });
    await apiClient.profile.profileControllerUpdateProfileMetadata({
      metadata: { [ANNOUNCEMENT_MEDIA_VIEWER_TOUR_METADATA_KEY]: true }
    });
  }, [updateUserProfile, user]);

  const onStartTour = useCallback(() => {
    onDismiss();
    setSearchParams(
      (prev) => {
        prev.set('startTour', 'announcement-media-viewer');
        return prev;
      },
      { replace: true }
    );
  }, [onDismiss, setSearchParams]);

  if (!isVisible) return null;

  return (
    <div
      className="media-viewer-tour-banner"
      style={{ height: DEMO_MODE_PANEL_HEIGHT }}
    >
      <strong>Media Viewer 2.0.</strong>
      See what&apos;s new in the updated media viewer
      <button
        type="button"
        className="media-viewer-tour-banner_btn"
        onClick={onStartTour}
      >
        Start a quick tour
      </button>
      <button
        type="button"
        className="media-viewer-tour-banner_close-btn"
        onClick={onDismiss}
        aria-label="Dismiss announcement"
      >
        <CloseSvg width={20} height={20} />
      </button>
    </div>
  );
}
